export const BUSINESS_START_HOUR = 9;
export const BUSINESS_END_HOUR = 18;

export function parseTimeString(timeString) {
  if (!timeString || typeof timeString !== 'string') return null;

  const parts = timeString.split(':');
  if (parts.length < 2) return null;

  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return { hour, minute };
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getBusinessEnd(forDate) {
  const end = new Date(forDate);
  end.setHours(BUSINESS_END_HOUR, 0, 0, 0);
  return end;
}

export function isTechnicianOnTimeOff(technicianId, date, timeOffEvents, time = null) {
  const dateStr = new Date(date).toISOString().split('T')[0];

  return timeOffEvents.some((event) => {
    if (event.technician_id !== technicianId) return false;

    const eventStartDate = new Date(event.start_date).toISOString().split('T')[0];
    const eventEndDate = event.end_date
      ? new Date(event.end_date).toISOString().split('T')[0]
      : eventStartDate;

    if (dateStr < eventStartDate || dateStr > eventEndDate) return false;
    if (event.is_all_day) return true;

    if (time && event.start_time && event.end_time) {
      const timeStr = typeof time === 'string' ? time : `${time.hour}:${time.minute}`;
      return timeStr >= event.start_time && timeStr <= event.end_time;
    }

    return false;
  });
}

export function calculateAvailableTechnicians(slot, { technicians = [], appointments = [], timeOffEvents = [] }) {
  if (!technicians.length) return 0;

  const slotTimeParts = slot?.time?.split(' - ');
  if (!slotTimeParts || slotTimeParts.length !== 2) return technicians.length;

  const slotStartTime = parseTimeString(slotTimeParts[0]?.trim());
  const slotEndTime = parseTimeString(slotTimeParts[1]?.trim());

  if (!slotStartTime || !slotEndTime) return technicians.length;

  const slotDate = new Date(slot.date);
  const slotStart = new Date(slotDate);
  slotStart.setHours(slotStartTime.hour, slotStartTime.minute, 0, 0);

  const slotEnd = new Date(slotDate);
  slotEnd.setHours(slotEndTime.hour, slotEndTime.minute, 0, 0);

  const unavailableTechnicianIds = new Set();

  technicians.forEach((tech) => {
    if (isTechnicianOnTimeOff(tech.id, slotDate, timeOffEvents, slotStartTime)) {
      unavailableTechnicianIds.add(tech.id);
    }
  });

  appointments.forEach((appt) => {
    if (!appt.technician) return;
    if (!appt.scheduled_date || !appt.scheduled_time_start || !appt.scheduled_time_end) return;

    const apptStartTime = parseTimeString(appt.scheduled_time_start);
    const apptEndTime = parseTimeString(appt.scheduled_time_end);

    if (!apptStartTime || !apptEndTime) return;

    const apptDate = new Date(appt.scheduled_date);
    const apptStart = new Date(apptDate);
    apptStart.setHours(apptStartTime.hour, apptStartTime.minute, 0, 0);

    const apptEnd = new Date(apptDate);
    apptEnd.setHours(apptEndTime.hour, apptEndTime.minute, 0, 0);

    const sameDay = apptDate.toDateString() === slotDate.toDateString();
    const overlaps = apptStart < slotEnd && apptEnd > slotStart;

    if (sameDay && overlaps) {
      unavailableTechnicianIds.add(appt.technician);
    }
  });

  return Math.max(technicians.length - unavailableTechnicianIds.size, 0);
}

export function generateTimeSlots({
  forDate = new Date(),
  urgencyId,
  durationMinutes = 60,
  now = new Date(),
} = {}) {
  const slots = [];
  const day = new Date(forDate);
  day.setHours(0, 0, 0, 0);
  const businessEnd = getBusinessEnd(day);
  const twoHoursFromNow =
    urgencyId === 'within-hour' ? new Date(now.getTime() + 2 * 60 * 60 * 1000) : null;

  const slotStepHours = durationMinutes / 60;

  for (let hour = BUSINESS_START_HOUR; hour < BUSINESS_END_HOUR; hour += slotStepHours) {
    const start = new Date(day);
    start.setHours(Math.floor(hour), Math.round((hour % 1) * 60), 0, 0);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

    if (end > businessEnd) continue;
    if (isSameDay(day, now) && start <= now) continue;
    if (urgencyId === 'within-hour' && isSameDay(day, now) && start > twoHoursFromNow) continue;

    const hh = start.getHours().toString().padStart(2, '0');
    const mm = start.getMinutes().toString().padStart(2, '0');
    const eh = end.getHours().toString().padStart(2, '0');
    const em = end.getMinutes().toString().padStart(2, '0');

    slots.push({
      id: `slot-${day.toISOString().slice(0, 10)}-${hh}-${mm}`,
      date: start.toISOString(),
      time: `${hh}:${mm} - ${eh}:${em}`,
      available: true,
    });
  }

  return slots;
}

export function getAvailableSlots({
  forDate = new Date(),
  urgencyId,
  durationMinutes = 60,
  technicians = [],
  appointments = [],
  timeOffEvents = [],
  now = new Date(),
} = {}) {
  const context = { technicians, appointments, timeOffEvents };

  return generateTimeSlots({ forDate, urgencyId, durationMinutes, now })
    .map((slot) => ({
      ...slot,
      techniciansAvailable: calculateAvailableTechnicians(slot, context),
    }))
    .filter((slot) => slot.available && slot.techniciansAvailable > 0);
}

export function hasAvailableSlotsForUrgency(urgencyId, {
  durationMinutes = 60,
  technicians = [],
  appointments = [],
  timeOffEvents = [],
  now = new Date(),
} = {}) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  return getAvailableSlots({
    forDate: today,
    urgencyId,
    durationMinutes,
    technicians,
    appointments,
    timeOffEvents,
    now,
  }).length > 0;
}
